import React from 'react';
import {Virtuoso} from 'react-virtuoso';
import {Box, Slide} from '@mui/material';
import {grey} from '@mui/material/colors';
import {Sx} from 'app/types/common';
import {FilesToolbar} from 'app/components/widgets/files';
import {Files, FilesApi, ListHeader, NoRecordings} from 'app/components/entities/files';
import {AnyDeviceModelType} from 'app/components/DeviceDetails/Models/Fabric';
import {
  BatchRecordings,
  RecordingCard,
  useFilterAndSortFiles,
  useSelectRecordings,
} from 'app/components/features/files';
import {Notifications} from 'app/components/Notifications';
import {downloadFile} from 'app/util/download';
import {isNil} from 'app/util/isNil';

interface Props extends Sx {
  teamId: string;
  recordings: Files.Recording[];
  displayDevice: boolean;
  devices?: Map<string, AnyDeviceModelType>;
}

export function FilesLibrary({sx, teamId, recordings, devices, displayDevice}: Props) {
  const {filtered, search, period, sharedOnly, setPeriod, setSharedOnly, setSearch} =
    useFilterAndSortFiles(recordings);
  const {selected, selectable, allSelected, deselectAll, selectAll, isSelected, toggle} =
    useSelectRecordings(filtered);

  const handleSelectAll = () => {
    if (allSelected) {
      deselectAll();
      return;
    }

    selectAll();
  };

  const handleDelete = async (id: string) => {
    try {
      await FilesApi.deleteRecording(id);
    } catch (e) {
      Notifications.addErrorNotification('Failed to delete recording');
      throw e;
    }
  };

  const handleDownload = async (id: string, name: string) => {
    const url = FilesApi.getDownloadUrl(teamId, id);
    downloadFile(url, name);
  };

  const handleShare = async (fileId: string) => {
    try {
      await FilesApi.shareFile(teamId, fileId);
    } catch (e) {
      Notifications.addErrorNotification('Failed to share recording');
      throw e;
    }
  };

  const handleRevokeShare = async (fileId: string) => {
    try {
      await FilesApi.revokeShare(teamId, fileId);
    } catch (e) {
      Notifications.addErrorNotification('Failed to share recording');
      throw e;
    }
  };

  const totalSelected = selected.length;
  const hasSelected = totalSelected > 0;
  const hasSelectable = selectable.length > 0;
  const hasFiltered = filtered.length > 0;
  const preparingDevices = isNil(devices);

  const renderList = () => {
    if (!hasFiltered) {
      return (
        <NoRecordings
          sx={{borderTop: `1px solid ${grey[300]}`, pt: 4}}
          sharedOnly={sharedOnly}
          period={period}
        />
      );
    }

    return (
      <>
        <ListHeader sx={{mb: 0.5}} showUnit={displayDevice} />

        <Virtuoso
          data={filtered}
          useWindowScroll={true}
          itemContent={(_index, r) => {
            const {id, name} = r;

            return (
              <RecordingCard
                key={id}
                dataId={id}
                selected={isSelected(id)}
                recording={r}
                device={devices?.get(r.deviceId)}
                loadingDevice={preparingDevices}
                showUnit={displayDevice}
                onSelect={() => toggle(id)}
                onShare={() => handleShare(id)}
                onDelete={() => handleDelete(id)}
                onDownload={() => handleDownload(id, name)}
                onRevokeSharing={() => handleRevokeShare(id)}
              />
            );
          }}
        />
      </>
    );
  };

  return (
    <Box sx={sx}>
      <FilesToolbar
        recordings={filtered}
        allSelected={allSelected}
        hasSelectable={hasSelectable}
        search={search}
        period={period}
        sharedOnly={sharedOnly}
        onSharedOnly={setSharedOnly}
        onSearch={setSearch}
        onSetPeriod={setPeriod}
        onSelectAll={handleSelectAll}
      />

      {renderList()}

      <Slide in={hasSelected} direction="up" unmountOnExit={true}>
        <BatchRecordings teamId={teamId} recordings={selected} onClose={deselectAll} />
      </Slide>
    </Box>
  );
}
